import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useApiSend } from '../../utils/httpClientTs';
import { changePassword } from '../../endpoints/login';
import AlertItem from '../../Components/AlertItem';
import { Image } from 'mui-image';
import logo from './../../assets/PEO_InformalLogo_Color.png';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  ChangePasswordRequest,
  ChangePasswordResponse,
} from '../../types/auth';
import { resolvePostLoginDestination } from '../../utils/personas';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  // Detect flow type: temp password flow vs normal password change flow
  // Temp password flow: User was redirected from login with requires_password_change: true
  // Normal flow: User navigated directly to change password page
  const isTempPasswordFlow = useMemo(() => {
    // Check if returnUrl exists (set during login redirect for temp password)
    const returnUrl = localStorage.getItem('returnUrl');
    // Check URL parameter as fallback
    const flowParam = searchParams.get('flow');
    // If returnUrl exists or flow=temp, it's temp password flow
    return !!returnUrl || flowParam === 'temp';
  }, [searchParams]);

  // Check if user is logged in, if not redirect to login
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const handleToggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const { mutate } = useApiSend<ChangePasswordResponse, ChangePasswordRequest>(
    changePassword,
    (data) => {
      setShowAlert(true);
      setIsSubmitting(false);
      setDisabled(true);
      // Redirect after 2 seconds
      setTimeout(() => {
        if (isTempPasswordFlow) {
          const destination = resolvePostLoginDestination(
            localStorage.getItem('returnUrl'),
            localStorage.getItem('default_route')
          );
          localStorage.removeItem('returnUrl');
          navigate(destination, { replace: true });
        } else {
          // Normal flow: redirect to user's profile or dashboard
          const userId = localStorage.getItem('user_id');
          const redirectUrl = userId ? `/user/${userId}` : '/';
          navigate(redirectUrl, { replace: true });
        }
      }, 2000);
    },
    (error: any) => {
      // Handle error response
      const errorMessage =
        error?.message ||
        error?.error ||
        'Error updating password, please try again later.';
      setError(errorMessage);
      setIsSubmitting(false);
    }
  );

  const validatePassword = (password: string): string | null => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters long';
    }
    if (password.length > 128) {
      return 'Password must be less than 128 characters long';
    }
    if (password.includes(' ')) {
      return 'Password cannot contain spaces';
    }
    if (password.includes('.')) {
      return 'Password cannot contain periods';
    }
    // check if password contains at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    // check if password contains at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    // check if password contains at least one number
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    // check if password contains at least one special character
    if (!/[!@#$%^&*]/.test(password)) {
      return 'Password must contain at least one special character (!@#$%^&*)';
    }
    return null;
  };

  const handleSubmit = (): void => {
    setError(null);

    // For normal password change flow, require current password
    if (!isTempPasswordFlow && !currentPassword.trim()) {
      setError('Current password is required');
      return;
    }

    // Validate new password
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    // Build request
    const requestData: ChangePasswordRequest = {
      new_password: newPassword,
    };

    // Include current_password based on flow type
    if (!isTempPasswordFlow) {
      // Normal flow: always include current_password
      requestData.current_password = currentPassword;
    } else if (currentPassword.trim()) {
      // Temp flow: only include if provided (should usually be empty)
      requestData.current_password = currentPassword;
    }

    mutate(requestData);
  };

  return (
    <Grid
      container
      direction={'column'}
      alignItems="center"
      sx={{ verticalAlign: 'middle', minHeight: '100vh', pt: 5 }}
    >
      <Grid item xs={12} p={5}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2, maxWidth: '500px' }}>
          <CardHeader
            title={
              isTempPasswordFlow ? 'Change Your Password' : 'Change Password'
            }
            sx={{ p: 1 }}
          />
          <CardContent sx={{ p: 1 }}>
            <Grid
              container
              spacing={2}
              direction={'column'}
              alignItems="center"
              sx={{ verticalAlign: 'middle' }}
            >
              <Grid
                item
                xs={12}
                textAlign={'center'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Image
                  src={logo}
                  alt="Logo"
                  sx={{ maxWidth: '300px' }}
                  width={'40vw'}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {isTempPasswordFlow
                    ? 'You must change your password to continue. Please set a new password below.'
                    : 'Enter your current password and a new password below.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  Password must be at least 12 characters long and contain at
                  least one uppercase letter, one lowercase letter, one number,
                  and one special character.
                </Typography>
              </Grid>
              {!isTempPasswordFlow && (
                <Grid item xs={12} paddingTop={2}>
                  <TextField
                    label="Current Password"
                    variant="outlined"
                    fullWidth
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    disabled={disabled}
                    size={'small'}
                    required
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    helperText="Enter your current password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            onClick={handleToggleCurrentPasswordVisibility}
                            edge="end"
                            disabled={disabled}
                          >
                            {showCurrentPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} paddingTop={2}>
                <TextField
                  label="New Password"
                  variant="outlined"
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  disabled={disabled}
                  size={'small'}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={handleToggleNewPasswordVisibility}
                          edge="end"
                          disabled={disabled}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} paddingTop={2}>
                <TextField
                  label="Confirm New Password"
                  variant="outlined"
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  disabled={disabled}
                  size={'small'}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleToggleConfirmPasswordVisibility}
                          edge="end"
                          disabled={disabled}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} paddingTop={2} textAlign={'center'}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  size="small"
                  loading={isSubmitting}
                  disabled={isSubmitting || disabled}
                >
                  Change Password
                </LoadingButton>
              </Grid>
              <Grid item xs={12} paddingTop={2}>
                {error && (
                  <AlertItem
                    severity="error"
                    description={error}
                    onClose={() => setError(null)}
                  />
                )}
                {showAlert && (
                  <AlertItem
                    severity="success"
                    description="Password changed successfully. Redirecting..."
                    onClose={() => setShowAlert(false)}
                    keepOpen={true}
                  />
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ChangePassword;
